import { useMemo } from 'react';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import type { Role, UserDetail } from '~/modules/settings/api/lib/schemas';
import { sortByPriority } from '~/modules/settings/utils/roles';

import { UserDetailContext } from './UserDetailContext';

interface UserDetailProviderProps {
  user: UserDetail;
  children: React.ReactNode;
}

export function UserDetailProvider({
  user,
  children,
}: UserDetailProviderProps) {
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);
  const { rolesMap, roleResources } = user;
  const rolesByResource = useMemo(() => {
    const labelMap = new Map<string, Role[]>();

    if (!isInternalUserUpdatesEnabled) {
      for (const [role, resources] of Object.entries(rolesMap ?? {})) {
        for (const resource of resources.labels) {
          labelMap.set(`${resource.id}-LABEL`, [
            ...(labelMap.get(`${resource.id}-LABEL`) || []),
            role as Role,
          ]);
        }

        for (const resource of resources.labelGroups) {
          labelMap.set(`${resource.id}-RESOURCE_GROUP`, [
            ...(labelMap.get(`${resource.id}-RESOURCE_GROUP`) || []),
            role as Role,
          ]);
        }
      }
    } else {
      for (const [role, resources] of Object.entries(roleResources)) {
        for (const resource of resources) {
          labelMap.set(`${resource.id}-${resource.type}`, [
            ...(labelMap.get(`${resource.id}-${resource.type}`) || []),
            role as Role,
          ]);
        }
      }
    }

    labelMap.forEach((roles, labelId) => {
      labelMap.set(labelId, sortByPriority(roles));
    });

    return labelMap;
  }, [isInternalUserUpdatesEnabled, rolesMap, roleResources]);

  return (
    <UserDetailContext.Provider value={{ ...user, rolesByResource }}>
      {children}
    </UserDetailContext.Provider>
  );
}

UserDetailProvider.displayName = 'UserDetailProvider';
